import { type FormVariant, Prompt, twMerge, Form as UiForm } from '@faceup/ui'
import { Button } from '@faceup/ui-base'
import { useId } from '@mantine/hooks'
import type { ReactNode } from 'react'
import type { FieldValues } from 'react-hook-form'
import { useFormLocalization } from '../FormLocalizationProvider'
import { FormItemsWrapper } from './FormItemsWrapper'
import { type ReactHookFormProps, useFormLogic } from './useFormLogic'

export type FormProps<FV extends FieldValues = FieldValues> = {
  children: ReactNode
  variant?: FormVariant
  isUnsavedAlertDisabled?: boolean
  submitButtonWrapperClassName?: string
  /** Hides the submit button. In the `affix` layout this also hides the sibling Cancel button. */
  hideSubmitButtons?: boolean
} & (
  | ({ buttonsPosition?: 'affix' } & Omit<
      ReactHookFormProps<FV>,
      'submitButtonText' | 'customSubmitButtonText'
    >)
  | ({ buttonsPosition: 'under' | 'under-center' | 'under-block' } & ReactHookFormProps<FV>)
)

const justifyContentMap = {
  'under-center': 'center',
  'under-block': '',
  under: 'flex-end',
}

export const Form = <FV extends FieldValues = FieldValues>({
  children,
  buttonsPosition = 'affix',
  isUnsavedAlertDisabled,
  variant,
  submitButtonWrapperClassName,
  hideSubmitButtons = false,
  ...props
}: FormProps<FV>) => {
  const {
    modalForm,
    unsavedChanges,
    leaveUnsavedChanges,
    leaveUnsavedChangesTitle,
    submitButton: { save },
  } = useFormLocalization()
  const formLogicProps: ReactHookFormProps<FV> = (
    buttonsPosition === 'affix' ? { ...props, submitButtonText: 'send' } : props
  ) as ReactHookFormProps<FV>
  const {
    submitButtonText,
    onSubmit,
    isLoading,
    additionalButtons,
    isSubmitButtonDisabled,
    hasUnsavedChanges,
    dirtyFieldsCount,
    reset,
  } = useFormLogic(formLogicProps)
  const formId = useId()

  // We need to allow redirect if onSubmit succeeds, IDK if this can be written in a better way
  const showUnsavedAlert = hasUnsavedChanges && !isLoading

  return (
    <UiForm variant={variant} id={formId} onSubmit={onSubmit} className='w-full'>
      {!isUnsavedAlertDisabled && (
        <Prompt
          when={showUnsavedAlert}
          message={leaveUnsavedChanges}
          title={leaveUnsavedChangesTitle}
        />
      )}
      {buttonsPosition === 'affix' ? (
        <div className='mb-[-16px]'>
          <FormItemsWrapper>{children}</FormItemsWrapper>
          <div className='pointer-events-none sticky bottom-0 bg-white/90 py-4'>
            <div className='flex items-center justify-end gap-6'>
              <div
                className={twMerge(
                  'transition-opacity',
                  dirtyFieldsCount === 0 ? 'opacity-0' : 'opacity-100'
                )}
              >
                {unsavedChanges(dirtyFieldsCount)}
              </div>
              <div className='pointer-events-auto flex flex-row gap-2'>
                {additionalButtons}
                {!hideSubmitButtons && (
                  <>
                    <Button onClick={() => reset()} disabled={isSubmitButtonDisabled}>
                      {modalForm.cancelButton}
                    </Button>
                    <Button
                      type='primary'
                      htmlType='submit'
                      form={formId}
                      loading={isLoading}
                      disabled={isSubmitButtonDisabled}
                      data-test='form-submit-button'
                    >
                      {save}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <FormItemsWrapper>
          {children}
          <div
            className={twMerge('flex gap-4', submitButtonWrapperClassName)}
            style={{
              justifyContent: justifyContentMap[buttonsPosition],
            }}
          >
            {additionalButtons}
            {!hideSubmitButtons && (
              <Button
                type='primary'
                htmlType='submit'
                loading={isLoading}
                disabled={isSubmitButtonDisabled}
                data-test='form-submit-button'
                block={buttonsPosition === 'under-block'}
              >
                {submitButtonText}
              </Button>
            )}
          </div>
        </FormItemsWrapper>
      )}
    </UiForm>
  )
}
